import { useState, useEffect } from 'react';
import { authPostRequest } from 'Api/api';
import SuccessError from 'components/SuccessError';
import { useSelector } from 'react-redux';
import ModalComp from 'components/Modal';
import PropTypes from 'prop-types';
import Loader from 'components/Loader';
import CenteredWrapper from 'pages/updatePassword/CenteredWrapper';
import MarkupPriceForm from 'components/MarkupPriceForm';
import { isPrecise, integerPartLengthExceeds, isNegative, formatTier } from 'utils/utils';

const ValidateFields = (markupPrice, handleError) => {
  if (markupPrice === '') {
    handleError('markupPrice', 'Please enter tier percentage');
    return false;
  }
  const markupPercentage = isPrecise(markupPrice, 2);
  if (markupPercentage) {
    handleError('markupPrice', 'Please enter 2 digits after decimal i.e xxx.xx');
  }

  const isValidLength = integerPartLengthExceeds(markupPrice, 3);
  if (isValidLength) {
    handleError('markupPrice', 'Maximum 3 digits allowed before decimal i.e xxx.xx');
  }

  const negCheck = isNegative(markupPrice);
  if (negCheck) {
    handleError('markupPrice', 'Please enter positive value for tier percentage');
  }

  if (markupPrice >= 0 && !markupPercentage && !isValidLength && !negCheck) {
    return true;
  }
  return false;
};

const CreateAndUpdateMarkupPrice = ({
  currentTier,
  tierName,
  addOpenModal,
  setAddOpenModal,
  getBracketPriceList,
  setAlertBar,
  markup
}) => {
  const [showLoader, setShowLoader] = useState(false);
  const [showAlertBar, setShowAlertBar] = useState(false);
  const [markupPrice, setMarkupPrice] = useState(markup ?? 0);
  const [successMessage, setSuccessMessage] = useState([]);
  const [errorMessage, setErrorMessage] = useState([]);
  const [errors, setErrors] = useState({});
  const [disableSave, setDisableSave] = useState(true);
  const [disableActive, setDisableActive] = useState(false);
  const [disableComponent, setDisableComponent] = useState(false);
  const { userData, storeData, adminAccess } = useSelector((store) => store.auth);

  useEffect(() => {
    if (tierName === currentTier) {
      setDisableActive(true);
      setDisableSave(true);
    }
  }, []);

  const handleMarkupInfo = (e) => {
    setDisableSave(false);
    setDisableActive(true);
    setMarkupPrice(e.target.value);
    handleError(e.target.name, null);
  };

  const handleError = (input, errorMessage) => {
    setErrors((prevState) => ({ ...prevState, [input]: errorMessage }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage([]);
    setSuccessMessage([]);
    setShowAlertBar(false);
    setAlertBar(false);
    const isFormValid = ValidateFields(markupPrice, handleError);
    try {
      const requestParams = {
        userId: userData.userId,
        storeId: adminAccess ? storeData?.id : userData?.storesList[0]?.storeId,
        currentTierPercentage: markupPrice,
        currentTier: tierName
      };
      setShowLoader(true);

      const createMarkup = isFormValid && (await authPostRequest('saveMarkup', requestParams));
      setShowLoader(false);
      if (createMarkup?.resStatus === 1) {
        setSuccessMessage([createMarkup?.resMessage]);
        setDisableComponent(true);
        setTimeout(() => {
          setAddOpenModal(false);
          getBracketPriceList();
        }, [3000]);
      }
      if (createMarkup?.resStatus === 0) {
        setErrorMessage([createMarkup?.resMessage]);
      }
      setShowAlertBar(true);
      setAlertBar(true);
    } catch (err) {
      setShowLoader(false);
      setErrorMessage([...errorMessage, 'Error']);
    }
  };

  return (
    <CenteredWrapper>
      <ModalComp
        modalWidth={350}
        open={addOpenModal}
        setOpen={setAddOpenModal}
        header={`Update ${formatTier(tierName)}`}>
        {showAlertBar && (
          <SuccessError
            successMessage={successMessage}
            errorMessage={errorMessage}
            setShowAlertBar={setShowAlertBar}
          />
        )}
        {showLoader && <Loader open={showLoader} />}
        <MarkupPriceForm
          handleSubmit={handleSubmit}
          handleError={handleError}
          handleMarkupInfo={handleMarkupInfo}
          markupPrice={markupPrice}
          error={errors}
          disableActive={disableActive}
          disableSave={disableSave}
          disableComponent={disableComponent}
        />
      </ModalComp>
    </CenteredWrapper>
  );
};

CreateAndUpdateMarkupPrice.propTypes = {
  addOpenModal: PropTypes.bool,
  setAddOpenModal: PropTypes.func,
  isFormSaved: PropTypes.bool,
  setIsFormSaved: PropTypes.func,
  getBracketPriceList: PropTypes.func,
  setAlertBar: PropTypes.func,
  markup: PropTypes.number,
  tierName: PropTypes.string,
  currentTier: PropTypes.string
};

export default CreateAndUpdateMarkupPrice;
